// components/Attachments.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Radio,
  Stack,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { toast } from "react-toastify";
import { Button, Typography } from "Atoms";
import AttachmentManager from "Atoms/AttachmentManager";
import {
  fetchTestPlan,
  postGenerateTestPlan,
} from "Zustand/services/attachmentService";
import { useBoundStore } from "Zustand/store/mainStore";
import { DOC_EXTS } from "Utils/helperFunctions";
// If you keep API services split, you can move these 2 calls into your services layer.

const TEST_PLAN_TEMPLATE_SPECIFIER = "testplantemplate";

type ApiAttachment = {
  attachment: {
    title: string;
    description: string;
    specifier?: string;
    file: {
      fileid: string;
      fileversion: string;
      url: string;
      mimetype?: string;
    };
    nodeid: string; // attachment nodeid
  };
};

type AttachmentAtom = {
  id: string; // local ID for the atom
  title: string;
  description: string;
  file?: File;
  url?: string;
  mimetype?: string;
  nodeid?: string; // attachment nodeid
  specifier?: string;
};

function normalizeAttachments(
  apiItems: ApiAttachment[] | undefined
): AttachmentAtom[] {
  if (!apiItems || apiItems.length === 0) return [];
  return apiItems.map((it) => ({
    id: it.attachment.file.fileid, // stable unique
    title:
      it.attachment.title ||
      it.attachment.file.fileid.split("/").pop() ||
      "untitled",
    description: it.attachment.description || "",
    url: it.attachment.file.url,
    mimetype: it.attachment.file.mimetype,
    nodeid: it.attachment.nodeid,
    specifier: it.attachment.specifier || "",
  }));
}

export default function Attachments({
  testPlanNodeId,
}: {
  testPlanNodeId: string; // e.g. "247fn://harman.com/...."
}) {
  const { uploadAttachments, msalUsername } = useBoundStore();

  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploadingTemplates, setUploadingTemplates] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [attachments, setAttachments] = useState<AttachmentAtom[]>([]);
  const [selectedTemplateNodeId, setSelectedTemplateNodeId] = useState("");
  const [templateUploads, setTemplateUploads] = useState<AttachmentAtom[]>([]);

  const testPlanDocuments = useMemo(
    () =>
      attachments.filter(
        (attachment) => attachment.specifier !== TEST_PLAN_TEMPLATE_SPECIFIER
      ),
    [attachments]
  );

  const testPlanTemplates = useMemo(
    () =>
      attachments.filter(
        (attachment) => attachment.specifier === TEST_PLAN_TEMPLATE_SPECIFIER
      ),
    [attachments]
  );

  const loadPlan = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await fetchTestPlan(testPlanNodeId);
      const normalizedAttachments = normalizeAttachments(
        res?.testplan?.hasattachment
      );

      setAttachments(normalizedAttachments);
      setSelectedTemplateNodeId((currentSelection) => {
        if (
          normalizedAttachments.some(
            (attachment) => attachment.nodeid === currentSelection
          )
        ) {
          return currentSelection;
        }

        return "";
      });
    } catch (e: any) {
      console.error(e);
      setError(e?.message || "Failed to load testplan.");
    } finally {
      setLoading(false);
    }
  }, [testPlanNodeId]);

  const handleGenerate = useCallback(async () => {
    if (!selectedTemplateNodeId) {
      setTabIndex(1);
      toast.error("Please choose a test plan template before generating.");
      return;
    }

    setError(null);
    setGenerating(true);
    try {
      await postGenerateTestPlan(testPlanNodeId, {
        testplantemplatenodeid: selectedTemplateNodeId,
        username: msalUsername || undefined,
      });
      await loadPlan(); // refresh attachments
    } catch (e: any) {
      console.error(e);
      setError(e?.message || "Failed to generate testplan.");
    } finally {
      setGenerating(false);
    }
  }, [selectedTemplateNodeId, testPlanNodeId, loadPlan, msalUsername]);

  const handleTemplateUpload = useCallback(async () => {
    if (!templateUploads.length) return;

    const uploadableTemplates = templateUploads.filter(
      (attachment) => attachment.file
    );

    if (!uploadableTemplates.length) {
      toast.error("Please choose a template file to upload.");
      return;
    }

    setError(null);
    setUploadingTemplates(true);

    try {
      // Test plan templates are regular attachments. The specifier separates
      // them from generated test plan documents in the backend response.
      const uploadsWithSpecifier = uploadableTemplates.map((attachment) => ({
        ...attachment,
        specifier: TEST_PLAN_TEMPLATE_SPECIFIER,
        file: Object.assign(attachment.file as File, {
          title: attachment.title,
          description: attachment.description,
          specifier: TEST_PLAN_TEMPLATE_SPECIFIER,
        }),
      }));

      await uploadAttachments(testPlanNodeId, uploadsWithSpecifier, []);
      setTemplateUploads([]);
      toast.success("Test plan template uploaded successfully.");
      await loadPlan();
    } catch (e: any) {
      console.error(e);
      setError(e?.message || "Failed to upload test plan template.");
      toast.error(e?.message || "Failed to upload test plan template.");
    } finally {
      setUploadingTemplates(false);
    }
  }, [templateUploads, uploadAttachments, testPlanNodeId, loadPlan]);



  useEffect(() => {
    // initial load
    loadPlan();
  }, [loadPlan]);


  return (
    <Paper sx={{ p: 2 }} elevation={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">Test Plan Documents</Typography>

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={18} color="inherit" />
                <span>Generating…</span>
              </Stack>
            ) : (
              "Generate test plan"
            )}
          </Button>

          <Tooltip title="Reload attachments">
            <span>
              <IconButton onClick={loadPlan} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Tabs
        value={tabIndex}
        onChange={(_, value) => setTabIndex(value)}
        sx={{ mt: 2, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Test Plan Documents" />
        <Tab label="Test Plan Templates" />
      </Tabs>

      {error && (
        <Box mt={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading && (
        <Box mt={4} textAlign="center">
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading attachments…
          </Typography>
        </Box>
      )}

      {!loading && tabIndex === 0 && (
        <>
          {/* Read-only AttachmentManager: just for visual listing */}
          <Box mt={3}>
            <AttachmentManager
              _attachments={testPlanDocuments}
              onChange={() => {}}
              disableFileChangeForExisting
              defaultExpanded
              allowedFileExtensions={[]} // show any
              cropFeature={false}
              downloadAble={true}
              readOnly
            />
          </Box>


          {!testPlanDocuments.length && !error && (
            <Box mt={3} textAlign="center" color="text.secondary">
              <Typography variant="body2">
                No attachments yet. Generate the test plan to create documents.
              </Typography>
            </Box>
          )}
        </>
      )}

      {!loading && tabIndex === 1 && (
        <Stack spacing={3} sx={{ mt: 3 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6">Test Plan Templates</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Templates uploaded here are saved with the testplantemplate
              specifier.
            </Typography>

            <Box mt={2}>
              {testPlanTemplates.length > 0 && (
                <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                  {testPlanTemplates.map((template, index) => (
                    <Box
                      key={template.id}
                      onClick={() =>
                        template.nodeid &&
                        setSelectedTemplateNodeId(template.nodeid)
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        cursor: template.nodeid ? "pointer" : "default",
                        borderBottom:
                          index === testPlanTemplates.length - 1
                            ? "none"
                            : "1px solid",
                        borderColor: "divider",
                        bgcolor:
                          selectedTemplateNodeId === template.nodeid
                            ? "action.selected"
                            : "background.paper",
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Radio
                          checked={selectedTemplateNodeId === template.nodeid}
                          disabled={!template.nodeid}
                          onChange={() =>
                            template.nodeid &&
                            setSelectedTemplateNodeId(template.nodeid)
                          }
                        />
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {template.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {template.description || "(Optional text)"}
                          </Typography>
                        </Box>
                      </Stack>

                      <Tooltip title="Download template">
                        <span>
                          <IconButton
                            component="a"
                            href={template.url}
                            download
                            target="_blank"
                            rel="noreferrer"
                            disabled={!template.url}
                            onClick={(event) => event.stopPropagation()}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  ))}
                </Paper>
              )}
            </Box>

            {!testPlanTemplates.length && !error && (
              <Alert severity="info" sx={{ mt: 2 }}>
                No test plan templates uploaded yet.
              </Alert>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <AttachmentManager
              _attachments={templateUploads}
              onChange={(updated) => setTemplateUploads(updated)}
              defaultExpanded
              allowedFileExtensions={DOC_EXTS}
              cropFeature={false}
              title="Upload Test Plan Templates"
              downloadAble={false}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleTemplateUpload}
                disabled={!templateUploads.length || uploadingTemplates}
              >
                {uploadingTemplates ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={18} color="inherit" />
                    <span>Uploading...</span>
                  </Stack>
                ) : (
                  "Upload templates"
                )}
              </Button>
            </Box>
          </Paper>
        </Stack>
      )}
    </Paper>
  );
}
